import { ICitationStyleResponse, IExportFormatResponse } from '../../../pages/mylibrary/api/models/mylibrary';
import { ILibraryExportCitationsDialogStore } from '../../../pages/mylibrary/stores/libraryExportCitationsDialogStore';
import { LibraryExportOptionType } from '../../../pages/mylibrary/typings/enums';
import { ILibraryMainStore } from '../../../pages/mylibrary/typings/stores';
import { EXPORT_TOP_VALUE } from '../../../pages/mylibrary/utils/constants';
import useOnUpdate from '../../hooks/useOnUpdate';
import { IGlobalAlertStore } from '../../stores/globalAlertStore';
import { ExportQueryType } from '../../typings/enums';
import { Maybe } from '../../typings/types';
import { EventCategory } from '../../utils/googleAnalyticsConstants';
import CheckBox from '../formItems/checkBox';
import FormItemWithDropdown from '../formItems/formItemWithDropdown';
import InputWithSearchableDropdown from '../formItems/inputWithSearchableDropdown';
import ApplyButton from '../tags/applyButton';
import ModalWindow from './modalWindow';
import { Stack } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { Colors, IRupSelectItem } from 'copyright-clearance-center-common-ui';
import { RupNotification, RupRadio, useRupInput } from 'copyright-clearance-center-common-ui-react';
import { inject, observer } from 'mobx-react';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';

const useStyles = makeStyles(() =>
	createStyles({
		contentContainer: {
			display: 'flex',
			flexDirection: 'column',
			gap: '1rem'
		},
		optionsContainer: {
			display: 'flex',
			flexDirection: 'column',
			gap: '0.5rem'
		},
		fullWidthButton: {
			width: '100%',
			'& .rup-dropdown': {
				width: '100%',
				display: 'block',
				margin: 0,
				height: '2.25rem'
			},
			'& button': {
				width: '100%',
				display: 'flex',
				justifyContent: 'space-between',
				textAlign: 'left',
				height: '100% !important',
				color: `${Colors.neutral900} !important`
			}
		},
		dropdownMenu: {
			'& button.rup-btn--neutral.rup-btn--text': {
				color: `${Colors.neutral900} !important`
			}
		}
	})
);

export interface IExportCitationsDialogProps {
	isSelectedOnly?: boolean;
	hasCitationsSelected?: boolean;
	hasCustomFieldsConfigured?: boolean;
	onCancel: () => void;
	exportCitationsDialogStore?: ILibraryExportCitationsDialogStore;
	alertStore?: IGlobalAlertStore;
}

interface IExportCitationFormData {
	exportFormat: IExportFormatResponse;
	exportStyle: ICitationStyleResponse;
	exportScope: LibraryExportOptionType;
	fileName: Maybe<string>;
	includeAbstract: boolean;
	libraryId: number;
	documentIds: number[];
	queryParameters: string;
}

interface IExportCitationDialogData {
	exportFormat: IExportFormatResponse;
	exportStyle: ICitationStyleResponse;
	exportScope: LibraryExportOptionType;
	includeAbstract: boolean;
	fileName: string;
}

function getExportCitationParameters(
	exportFormData: IExportCitationFormData,
	queryType?: ExportQueryType
): Record<string, string> {
	const queryTypeValue = queryType ?? ExportQueryType.LIBRARY;
	const exportParameters: Record<string, string> = {
		StyleId: exportFormData.exportStyle?.id?.toString() ?? '',
		Format: exportFormData.exportFormat.formatKey,
		Top: EXPORT_TOP_VALUE.toString(),
		QueryType: queryTypeValue,
		IncludeAbstract: Boolean(exportFormData.includeAbstract).toString(),
		DocIds:
			exportFormData.exportScope === LibraryExportOptionType.SELECTED
				? exportFormData.documentIds?.join(',')
				: '',
		QueryParams: exportFormData.queryParameters ?? '',
		...(exportFormData.exportFormat.fileNameEnabled ? { FileName: exportFormData.fileName ?? '' } : {})
	};

	return exportParameters;
}

export const ExportCitationsDialog: FunctionComponent<IExportCitationsDialogProps> = ({
	exportCitationsDialogStore,
	hasCustomFieldsConfigured = false,
	hasCitationsSelected = false,
	...props
}) => {
	const classes = useStyles();
	const canSelectExportAll = !props.isSelectedOnly;
	const initExportFormat = () => {
		return exportCitationsDialogStore.formats?.[0] ?? null;
	};

	const [isInExportState, setIsInExportState] = useState(false);
	const [exportButtonEnabled, setExportButtonEnabled] = useState(false);

	const [exportFormat, setExportFormat] = useState<IExportFormatResponse>(initExportFormat);
	const [exportStyle, setExportStyle] = useState<ICitationStyleResponse>(null);
	const [includeAbstract, setIncludeAbstract] = useState(false);

	const formatItems = useMemo(() => {
		if (!exportCitationsDialogStore.formats?.length) {
			return [];
		}

		return exportCitationsDialogStore.formats.map(format => ({
			value: format.id.toString(),
			text: format.name
		}));
	}, [exportCitationsDialogStore.formats]);

	useEffect(() => {
		if (exportCitationsDialogStore.formats.length) {
			setExportFormat(exportCitationsDialogStore.formats[0]);
			return;
		}

		setExportFormat(null);
	}, [exportCitationsDialogStore.formats]);

	const [exportOption, setExportOption] = useState(() => {
		if (hasCitationsSelected && !canSelectExportAll) {
			return LibraryExportOptionType.SELECTED;
		}

		if (!hasCitationsSelected && canSelectExportAll) {
			return LibraryExportOptionType.ALL;
		}

		return hasCitationsSelected ? LibraryExportOptionType.SELECTED : LibraryExportOptionType.ALL;
	});

	const fetchCitationStyles = async (searchText: string, page: number, pageSize: number) => {
		const result = await exportCitationsDialogStore.searchStyles({
			searchText,
			page,
			pageSize
		});

		return {
			items: result.items.map(style => ({
				value: style.id.toString(),
				text: style.name
			})),
			hasMore: result.hasMore,
			total: result.total
		};
	};

	const [fileNameControl, fileNameValue] = useRupInput({
		id: 'fileNameControl',
		value: '',
		placeholder: localize('Enter a file name'),
		label: localize('File Name'),
		validationPredicate: () => true,
		testId: 'file-name-input'
	});

	const validateFormData = (dialogData: IExportCitationDialogData): boolean => {
		if (dialogData?.exportFormat?.id === null) {
			return false;
		}

		const format = dialogData.exportFormat;
		if (!format.requiresStyle) {
			return true;
		}

		return Boolean(dialogData.exportStyle?.id?.toString());
	};

	function collectDialogData(): IExportCitationDialogData {
		return {
			exportFormat: exportFormat,
			exportStyle: exportFormat.requiresStyle ? exportStyle : null,
			exportScope: exportOption,
			includeAbstract: exportFormat.includeAbstractEnabled && includeAbstract,
			fileName: exportFormat.fileNameEnabled ? fileNameValue : null
		};
	}

	useOnUpdate(() => {
		setExportButtonEnabled(validateFormData(collectDialogData()));
	}, [exportFormat, exportStyle, exportOption, includeAbstract, fileNameValue]);

	const handleExportOptionChange = (value: LibraryExportOptionType) => {
		if (hasCitationsSelected && canSelectExportAll) {
			setExportOption(value);
		}
	};

	const handleStyleSelect = (item: IRupSelectItem) => {
		const styleId = parseInt(item?.value, 10);
		const selectedStyle = exportCitationsDialogStore.styles.find(style => style.id === styleId);
		setExportStyle(selectedStyle ?? null);
	};

	const handleFormatChange = (value: string) => {
		const selectedFormat = exportCitationsDialogStore.formats.find(format => format.id.toString() === value);
		setExportFormat(selectedFormat ?? null);
	};

	const handleExport = () => {
		setIsInExportState(true);

		const exportFormData = {
			libraryId: exportCitationsDialogStore.libraryId,
			documentIds: exportCitationsDialogStore.documentIds,
			queryParameters: exportCitationsDialogStore.queryParameters,
			...collectDialogData()
		};

		exportCitationsDialogStore.doExport({
			useConfirmationToken: true,
			requestData: getExportCitationParameters(exportFormData, ExportQueryType.LIBRARY),
			successCallback: () => {
				setIsInExportState(false);
				gaLogAction(EventCategory.Library, 'ExportSuccessful');
				props.onCancel();
			},
			errorCallback(err: Error) {
				setIsInExportState(false);
				gaLogAction(EventCategory.Library, 'ExportFailed');
				setExportButtonEnabled(false);
				props.alertStore.error(err.message);
			}
		});
	};

	const actions = [
		<Stack key="actions" direction="row" flexWrap="nowrap" spacing={3}>
			<button
				className="rup-btn rup-btn--lg"
				data-test-id="export-citations-cancel-btn"
				type="button"
				onClick={props.onCancel}
				disabled={isInExportState}
			>
				{localize('Cancel')}
			</button>
			<ApplyButton
				data-test-id="export-citations-export-btn"
				applying={isInExportState}
				disabled={!exportButtonEnabled}
				onClick={handleExport}
				label={localize('Export')}
				large
			/>
		</Stack>
	];

	useEffect(() => {
		if (!exportCitationsDialogStore.dataLoadError) {
			return;
		}

		const errorMessage =
			exportCitationsDialogStore.errorMessage ?? localize('An unexpected error has occurred. Please try again.');
		props.alertStore.error(errorMessage);
		exportCitationsDialogStore.resetLastError();
	}, [exportCitationsDialogStore.dataLoadError]);

	const content = (
		<div className={classes.contentContainer}>
			{exportCitationsDialogStore.selectedItemsLimitExceeded && (
				<RupNotification
					text={localize(
						'You have selected {0} items, which exceeds selection limit of {1}. Only the Export All (limit of 1000 items) option will be available. Or click cancel to return to search results to select fewer items.',
						exportCitationsDialogStore.selectedItemsCount,
						exportCitationsDialogStore.selectedItemsLimit
					)}
					type="warning"
				/>
			)}
			<RupNotification
				text={localize('In case of duplicates, only unique results will be considered.')}
				type="information"
			/>

			{exportCitationsDialogStore.isLoading ? (
				<div>{localize('Loading...')}</div>
			) : (
				<div className={classes.optionsContainer}>
					<div className={classes.fullWidthButton}>
						<FormItemWithDropdown
							id="exportFormatControl"
							label={localize('Export Format')}
							dropdownProps={{
								value: (exportFormat?.id ?? '').toString(),
								options: formatItems,
								style: { width: '100%', margin: 0 },
								testId: 'export-format-select',
								menuProps: {
									maxHeight: '12.5rem',
									width: '33.875rem',
									className: classes.dropdownMenu
								},
								onChange: handleFormatChange
							}}
						/>
					</div>
					{Boolean(exportFormat?.fileNameEnabled) && fileNameControl}
					{Boolean(exportFormat?.requiresStyle) && (
						<InputWithSearchableDropdown
							id="citationStyleControl"
							label={localize('Citation Style')}
							className="rup-form-item__label"
							placeholder={localize('Please select...')}
							onSelect={handleStyleSelect}
							initialValue=""
							testId="citation-style-select"
							fetchItems={fetchCitationStyles}
							pageSize={20}
						/>
					)}
				</div>
			)}

			<div className={classes.optionsContainer}>
				<RupRadio
					id="exportOptionSelected"
					name="exportOptions"
					value={LibraryExportOptionType.SELECTED}
					label={localize('Export Selected')}
					testId="export-selected-radio"
					disabled={!hasCitationsSelected}
					checked={exportOption === LibraryExportOptionType.SELECTED}
					onCheckedChanged={checked => {
						if (checked) {
							handleExportOptionChange(LibraryExportOptionType.SELECTED);
						}
					}}
				/>
				<RupRadio
					id="exportOptionAll"
					name="exportOptions"
					value={LibraryExportOptionType.ALL}
					label={localize('Export All (limit of 1000 items)')}
					testId="export-all-radio"
					disabled={!canSelectExportAll}
					checked={exportOption === LibraryExportOptionType.ALL}
					onCheckedChanged={checked => {
						if (checked) {
							handleExportOptionChange(LibraryExportOptionType.ALL);
						}
					}}
				/>

				{Boolean(exportFormat?.includeAbstractEnabled) && (
					<CheckBox
						id="includeAbstractCheckbox"
						label={localize('Include Abstract')}
						checked={includeAbstract}
						onChange={setIncludeAbstract}
						testId="include-abstract-checkbox"
					/>
				)}
			</div>

			{hasCustomFieldsConfigured && (
				<RupNotification
					text={localize('Additional field sets are available in the CSV and XML formats only.')}
					type="information"
				/>
			)}
		</div>
	);

	return (
		<div>
			<ModalWindow
				title={localize('Export Citations')}
				width="37rem"
				content={content}
				actions={actions}
				closeHandler={props.onCancel}
				disableAutoClose
			></ModalWindow>
		</div>
	);
};

export default inject((stores: ILibraryMainStore) => ({
	exportCitationsDialogStore: stores.exportCitationsDialogStore,
	alertStore: stores.globalAlertStore
}))(observer(ExportCitationsDialog));
